import { useEffect, useState } from "react";
import { formatNumberToLocale } from "@utils/formatNumber";

import { causeLabel, confidenceLabel } from "./disbursementCoverage";
import {
  downloadDisbursementCoverage,
  fetchDisbursementCoverage,
} from "./disbursementCoverageApi";

const PAGE_SIZE = 50;
const CAUSES = [
  { value: "all", label: "Todos" },
  { value: "no_composition", label: "Sem composição" },
  { value: "remaining_disbursements", label: "Saldo não distribuído" },
  { value: "unclassified", label: "A revisar" },
];

const money = (value) =>
  value == null ? "—" : `R$ ${formatNumberToLocale(value)}`;

const percent = (value) =>
  value == null
    ? "—"
    : `${(value * 100).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

const saveBlob = (blob) => {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "cobertura-cronograma-desembolso.csv";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
};

const DisbursementCoverageOffcanvas = ({ open, onClose, projectId }) => {
  const [cause, setCause] = useState("all");
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: PAGE_SIZE,
    total: 0,
    hasMore: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (!open || !projectId) return undefined;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      const data = await fetchDisbursementCoverage(projectId, {
        cause,
        status,
        search: debouncedSearch,
        page: 0,
        pageSize: PAGE_SIZE,
      });
      if (cancelled) return;
      if (!data) {
        setRows([]);
        setError(true);
        setLoading(false);
        return;
      }
      setRows(data.rows ?? []);
      setPagination(
        data.pagination ?? {
          page: 0,
          pageSize: PAGE_SIZE,
          total: 0,
          hasMore: false,
        },
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, projectId, cause, status, debouncedSearch]);

  const loadMore = async () => {
    if (loading || !pagination.hasMore) return;
    setLoading(true);
    const nextPage = pagination.page + 1;
    const data = await fetchDisbursementCoverage(projectId, {
      cause,
      status,
      search: debouncedSearch,
      page: nextPage,
      pageSize: PAGE_SIZE,
    });
    if (!data) {
      setError(true);
      setLoading(false);
      return;
    }
    setRows((current) => [...current, ...(data.rows ?? [])]);
    setPagination(data.pagination ?? pagination);
    setLoading(false);
  };

  const download = async () => {
    if (downloading) return;
    setDownloading(true);
    const blob = await downloadDisbursementCoverage(projectId, {
      cause,
      status,
      search: search.trim(),
    });
    if (blob) saveBlob(blob);
    else setError(true);
    setDownloading(false);
  };

  return (
    <div className={open ? "offcanvas active" : "offcanvas"}>
      <div className="offcanvas-container large disbursement-coverage-offcanvas">
        <hgroup className="offcanvas-header">
          <div>
            <h2>Itens a ajustar no desembolso</h2>
            <p className="card-label">{`${pagination.total} ${
              pagination.total === 1 ? "serviço" : "serviços"
            } no filtro atual`}</p>
          </div>
          <button onClick={onClose} aria-label="Fechar">
            <i
              className="icon icon-width-24 icon-times background-accent"
              aria-hidden={true}
            ></i>
          </button>
        </hgroup>

        <div className="offcanvas-content">
          <div className="disbursement-coverage-controls">
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Buscar WBS ou serviço"
              aria-label="Buscar serviço"
            />
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value)}
              aria-label="Filtrar por estado"
            >
              <option value="all">Todos os estados</option>
              <option value="partial">Parcial</option>
              <option value="complete">Completo</option>
              <option value="unavailable">Indisponível</option>
              <option value="inconsistent">Inconsistente</option>
            </select>
            <button
              className="button button-accent small"
              onClick={download}
              disabled={downloading}
            >
              {downloading ? "Preparando…" : "Baixar lista completa"}
            </button>
          </div>

          <div
            className="offcanvas-buttons disbursement-coverage-causes"
            role="group"
            aria-label="Filtrar por causa"
          >
            {CAUSES.map((item) => (
              <button
                key={item.value}
                className={cause === item.value ? "active" : undefined}
                onClick={() => setCause(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div
            className="disbursement-coverage-table"
            role="region"
            aria-label="Serviços e cobertura"
            tabIndex={0}
          >
            <table>
              <thead>
                <tr>
                  <th>WBS</th>
                  <th>Serviço</th>
                  <th>Planejado</th>
                  <th>Desembolso</th>
                  <th>Saldo</th>
                  <th>Cobertura</th>
                  <th>Causa</th>
                  <th>Confiança</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item) => (
                  <tr key={item.wbsCodeId}>
                    <td className="coverage-wbs">
                      {item.wbs ?? item.wbsCodeId}
                    </td>
                    <td>{item.description || "—"}</td>
                    <td>{money(item.scheduledValue)}</td>
                    <td>{money(item.distributedValue)}</td>
                    <td
                      className={item.gapValue > 0 ? "coverage-gap" : undefined}
                    >
                      {money(item.gapValue)}
                    </td>
                    <td>{percent(item.percentage)}</td>
                    <td>{causeLabel(item.cause)}</td>
                    <td>{confidenceLabel(item.confidence)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {loading && <p className="card-label margin-top-16">Carregando…</p>}
          {error && !loading && (
            <p className="empty-state margin-top-16">
              Não foi possível carregar os itens. Tente novamente.
            </p>
          )}
          {!loading && !error && rows.length === 0 && (
            <p className="empty-state margin-top-16">
              Nenhum serviço neste filtro.
            </p>
          )}
          {pagination.hasMore && !loading && (
            <div className="flex justify-content-center margin-top-16">
              <button
                className="button button-secondary small"
                onClick={loadMore}
              >
                Carregar mais
              </button>
            </div>
          )}
        </div>
      </div>
      {open && <div onClick={onClose} className="offcanvas-background" />}
    </div>
  );
};

export default DisbursementCoverageOffcanvas;
